import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Layer, PixelData } from 'ag-psd'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  FileImage,
  GripVertical,
  Image as ImageIcon,
  Layers3,
  LoaderCircle,
  LockKeyhole,
  Maximize2,
  Minus,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  UploadCloud,
  X,
} from 'lucide-react'
import {
  composeLayers,
  getCompositeBounds,
  type CompositeBackground,
  type CompositeBoundsMode,
} from './lib/composite'
import {
  buildDocument,
  colorModeName,
  documentCompatibility,
  renderLayerPixels,
  toDisplayImageData,
  type LayerNode,
  type PsdDocument,
} from './lib/psd'
import { downloadBlob, downloadBytes, encodePng, safeFilename } from './lib/png'

type ExportFormat = 'png' | 'jpeg' | 'webp'

interface ExportSettings {
  filename: string
  format: ExportFormat
  bounds: CompositeBoundsMode
  scale: number
  background: CompositeBackground
  quality: number
  applyMask: boolean
  applyOpacity: boolean
  applyBlendModes: boolean
  embedProfile: boolean
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`
  const units = ['KB', 'MB', 'GB']
  let size = value / 1024
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size >= 100 ? size.toFixed(0) : size.toFixed(1)} ${units[unit]}`
}

function solidPixels(width: number, height: number, color: [number, number, number, number], inset = 0): PixelData {
  const data = new Uint8ClampedArray(width * height * 4)
  for (let y = inset; y < height - inset; y += 1) {
    for (let x = inset; x < width - inset; x += 1) {
      const index = (y * width + x) * 4
      data[index] = color[0]
      data[index + 1] = color[1]
      data[index + 2] = color[2]
      data[index + 3] = color[3]
    }
  }
  return { width, height, data }
}

async function createSampleFile() {
  const { writePsd } = await import('ag-psd')
  const width = 720
  const height = 480
  const bytes = writePsd({
    width,
    height,
    colorMode: 3,
    bitsPerChannel: 8,
    children: [
      { name: 'Lime accent', left: 450, top: 96, right: 642, bottom: 288, imageData: solidPixels(192, 192, [184, 241, 76, 255], 18) },
      { name: 'Surface', left: 120, top: 90, right: 600, bottom: 390, imageData: solidPixels(480, 300, [35, 40, 46, 255], 3), opacity: 0.92 },
      { name: 'Background', left: 0, top: 0, right: width, bottom: height, imageData: solidPixels(width, height, [14, 16, 19, 255]) },
    ],
    imageData: solidPixels(width, height, [14, 16, 19, 255]),
  }, { generateThumbnail: false })
  return new File([bytes], 'unlayer-sample.psd', { type: 'image/vnd.adobe.photoshop' })
}

function uniqueLeafIds(document: PsdDocument, selection: Set<string>) {
  const ids = new Set<string>()
  selection.forEach((id) => {
    const node = document.nodeMap.get(id)
    if (!node) return
    if (node.isGroup) node.descendants.forEach((childId) => ids.add(childId))
    else ids.add(id)
  })
  return [...ids].filter((id) => document.nodeMap.get(id)?.compatibility !== 'unsupported')
}

function canvasFromPixels(source: PixelData, scale = 1) {
  const sourceCanvas = window.document.createElement('canvas')
  sourceCanvas.width = source.width
  sourceCanvas.height = source.height
  sourceCanvas.getContext('2d')?.putImageData(toDisplayImageData(source), 0, 0)
  if (scale === 1) return sourceCanvas
  const canvas = window.document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(source.width * scale))
  canvas.height = Math.max(1, Math.round(source.height * scale))
  const context = canvas.getContext('2d')
  if (!context) throw new Error('画像レンダラーを初期化できませんでした。')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height)
  return canvas
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('画像をエンコードできませんでした。')), type, quality)
  })
}

function LayerThumbnail({ layer }: { layer: Layer }) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !layer.imageData) return
    const buffer = canvasFromPixels(layer.imageData)
    const context = canvas.getContext('2d')
    if (!context) return
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.imageSmoothingEnabled = true
    const scale = Math.min(canvas.width / buffer.width, canvas.height / buffer.height)
    const width = buffer.width * scale
    const height = buffer.height * scale
    context.drawImage(buffer, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height)
  }, [layer])

  return (
    <span className="layer-thumb checkerboard">
      {layer.imageData ? <canvas ref={ref} width={64} height={64} /> : <Layers3 size={15} />}
    </span>
  )
}

interface LayerRowProps {
  node: LayerNode
  active: boolean
  selected: boolean
  expanded: boolean
  reorderEnabled: boolean
  dragging: boolean
  onActivate: () => void
  onToggle: () => void
  onExpand: () => void
  onDragStart: () => void
  onDragEnd: () => void
  onDrop: (after: boolean) => void
}

function LayerRow({
  node,
  active,
  selected,
  expanded,
  reorderEnabled,
  dragging,
  onActivate,
  onToggle,
  onExpand,
  onDragStart,
  onDragEnd,
  onDrop,
}: LayerRowProps) {
  return (
    <div
      className={`layer-row ${active ? 'is-active' : ''} ${dragging ? 'is-dragging' : ''} ${node.compatibility === 'unsupported' ? 'is-disabled' : ''}`}
      style={{ '--depth': node.depth } as React.CSSProperties}
      onClick={onActivate}
      onDragOver={(event) => { if (reorderEnabled) event.preventDefault() }}
      onDrop={(event) => {
        event.preventDefault()
        const bounds = event.currentTarget.getBoundingClientRect()
        onDrop(event.clientY > bounds.top + bounds.height / 2)
      }}
    >
      <span
        className={`drag-handle ${reorderEnabled ? '' : 'is-disabled'}`}
        draggable={reorderEnabled}
        onDragStart={(event) => {
          event.stopPropagation()
          event.dataTransfer.effectAllowed = 'move'
          onDragStart()
        }}
        onDragEnd={onDragEnd}
        title={reorderEnabled ? 'ドラッグして同じ階層内で並び替え' : '検索中は並び替えできません'}
      ><GripVertical size={13} /></span>
      <button
        className={`check-button ${selected ? 'is-checked' : ''}`}
        onClick={(event) => { event.stopPropagation(); onToggle() }}
        aria-label={`${node.name}を合成対象にする`}
        disabled={node.compatibility === 'unsupported' && !node.isGroup}
      >
        {selected && <Check size={12} strokeWidth={3} />}
      </button>
      {node.isGroup ? (
        <button className="expand-button" onClick={(event) => { event.stopPropagation(); onExpand() }} aria-label={expanded ? 'グループを閉じる' : 'グループを開く'}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : <span className="expand-button is-placeholder" />}
      <LayerThumbnail layer={node.layer} />
      <span className="layer-copy">
        <span className="layer-name">{node.name}</span>
        <span className="layer-kind">{node.isGroup ? 'Group' : node.layer.text ? 'Type' : node.layer.placedLayer ? 'Smart object' : 'Pixel layer'}</span>
      </span>
      {node.compatibility === 'review' && <span className="warning-dot" title={node.compatibilityNote} />}
      {node.layer.hidden ? <Eye size={15} className="visibility is-off" /> : <Eye size={15} className="visibility" />}
    </div>
  )
}

interface CanvasPreviewProps {
  rendered?: PixelData
  label: string
}

function CanvasPreview({ rendered, label }: CanvasPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(100)

  const fit = useCallback(() => {
    const stage = stageRef.current
    if (!stage || !rendered) return
    const next = Math.min((stage.clientWidth - 96) / rendered.width, (stage.clientHeight - 96) / rendered.height, 1) * 100
    setZoom(Math.max(5, Math.round(next)))
  }, [rendered])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !rendered) return
    canvas.width = rendered.width
    canvas.height = rendered.height
    canvas.getContext('2d')?.putImageData(toDisplayImageData(rendered), 0, 0)
  }, [rendered])

  useEffect(() => { fit() }, [fit])

  return (
    <section className="canvas-area">
      <div className="canvas-toolbar">
        <div className="preview-label"><span className="status-pulse" />{label}</div>
        <div className="zoom-controls">
          <button onClick={() => setZoom((value) => Math.max(5, value - 10))} aria-label="縮小"><Minus size={15} /></button>
          <span>{zoom}%</span>
          <button onClick={() => setZoom((value) => Math.min(800, value + 10))} aria-label="拡大"><Plus size={15} /></button>
          <span className="toolbar-separator" />
          <button onClick={fit} aria-label="画面に合わせる"><Maximize2 size={15} /></button>
        </div>
      </div>
      <div className="canvas-stage" ref={stageRef}>
        {rendered ? (
          <div className="artboard checkerboard" style={{ width: rendered.width * zoom / 100, height: rendered.height * zoom / 100 }}>
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
          </div>
        ) : (
          <div className="empty-canvas"><ImageIcon size={28} /><span>プレビューできるレイヤーがありません</span></div>
        )}
      </div>
      <div className="canvas-statusbar">
        <span>{rendered ? `${rendered.width.toLocaleString()} × ${rendered.height.toLocaleString()} px` : '—'}</span>
        <span>RGBA composite preview</span>
      </div>
    </section>
  )
}

interface EmptyWorkspaceProps {
  onFile: (file: File) => void
  onSample: () => void
  error?: string
  loading: boolean
}

function EmptyWorkspace({ onFile, onSample, error, loading }: EmptyWorkspaceProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const acceptFile = (file?: File) => { if (file) onFile(file) }

  return (
    <main className="workspace empty-workspace">
      <header className="app-header">
        <div className="brand compact"><span className="brand-mark"><Layers3 size={17} /></span>Unlayer</div>
        <span className="header-divider" />
        <div className="file-title"><strong>No document</strong><span>PSD / PSB layer compositor</span></div>
        <div className="header-actions"><span className="local-badge"><LockKeyhole size={12} /> Local only</span></div>
      </header>
      <div className="workbench">
        <aside className="layers-panel panel empty-panel">
          <div className="panel-heading"><div><span className="panel-kicker">DOCUMENT</span><h2>Layers <em>0</em></h2></div></div>
          <label className="search-box is-disabled"><Search size={14} /><input disabled placeholder="レイヤーを検索" /></label>
          <div className="panel-empty"><Layers3 size={20} /><span>ドキュメントがありません</span></div>
          <div className="selection-footer"><span>0</span> selected for composite</div>
        </aside>
        <section className="canvas-area">
          <div className="canvas-toolbar"><div className="preview-label">Canvas</div></div>
          <div
            className={`canvas-stage empty-stage ${dragging ? 'is-dragging' : ''}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true) }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { event.preventDefault(); setDragging(false) }}
            onDrop={(event) => { event.preventDefault(); setDragging(false); acceptFile(event.dataTransfer.files[0]) }}
          >
            <input ref={inputRef} className="hidden-file-input" type="file" accept=".psd,.psb,image/vnd.adobe.photoshop" onChange={(event) => acceptFile(event.target.files?.[0])} />
            <div className="workbench-drop">
              <div className="workbench-drop-icon">{loading ? <LoaderCircle className="spin" size={25} /> : <UploadCloud size={25} />}</div>
              <strong>{loading ? 'Parsing document…' : 'Open a PSD or PSB document'}</strong>
              <span>{loading ? 'Processing locally in this browser' : 'Drop a file here, or choose a file from disk'}</span>
              <div className="workbench-drop-actions">
                <button className="primary-action" disabled={loading} onClick={() => inputRef.current?.click()}>ファイルを開く</button>
                <button disabled={loading} onClick={onSample}>サンプル</button>
              </div>
              {error && <div className="upload-error"><AlertTriangle size={14} />{error}</div>}
            </div>
          </div>
          <div className="canvas-statusbar"><span>Ready</span><span>Files never leave this device</span></div>
        </section>
        <aside className="export-panel panel empty-panel">
          <div className="panel-heading inspector-heading"><div><span className="panel-kicker">INSPECTOR</span><h2>Composite export</h2></div><Settings2 size={16} /></div>
          <section className="inspector-section document-summary">
            <div className="summary-icon"><FileImage size={18} /></div>
            <div><strong>PNG · JPEG · WebP</strong><span>Selection-based composite</span></div>
          </section>
          <section className="inspector-section empty-instructions">
            <h3>Workflow</h3>
            <ol><li>PSD / PSBを開く</li><li>合成するレイヤーを選択</li><li>必要ならドラッグで順序変更</li><li>設定を確認して画像を書き出す</li></ol>
          </section>
          <div className="export-footer"><button className="export-button" disabled><Download size={18} />レイヤーを選択</button><span>Image composite</span></div>
        </aside>
      </div>
    </main>
  )
}

interface ExportModalProps {
  document: PsdDocument
  file: File
  selectedNodes: LayerNode[]
  exporting: boolean
  onClose: () => void
  onExport: (settings: ExportSettings) => Promise<void>
}

function ExportModal({ document, file, selectedNodes, exporting, onClose, onExport }: ExportModalProps) {
  const canEmbedProfile = Boolean(document.iccProfile && document.psd.colorMode === 3)
  const [settings, setSettings] = useState<ExportSettings>({
    filename: `${file.name.replace(/\.(psd|psb)$/i, '')}-composite`,
    format: 'png',
    bounds: 'selection',
    scale: 1,
    background: 'transparent',
    quality: 0.92,
    applyMask: true,
    applyOpacity: true,
    applyBlendModes: true,
    embedProfile: canEmbedProfile,
  })
  const bounds = getCompositeBounds(selectedNodes, settings.bounds, document.psd.width, document.psd.height)
  const outputWidth = Math.max(1, Math.round(bounds.width * settings.scale))
  const outputHeight = Math.max(1, Math.round(bounds.height * settings.scale))
  const update = <K extends keyof ExportSettings>(key: K, value: ExportSettings[K]) => setSettings((current) => ({ ...current, [key]: value }))

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !exporting) onClose() }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [exporting, onClose])

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !exporting) onClose() }}>
      <section className="export-modal" role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
        <header className="modal-header">
          <div><span className="panel-kicker">COMPOSITE RENDER</span><h2 id="export-modal-title">画像を書き出す</h2></div>
          <button onClick={onClose} disabled={exporting} aria-label="閉じる"><X size={18} /></button>
        </header>
        <div className="modal-body">
          <div className="modal-main">
            <label className="field-label">ファイル名<input value={settings.filename} onChange={(event) => update('filename', event.target.value)} /></label>
            <div className="modal-field"><span className="field-label-text">形式</span><div className="format-grid">
              {(['png', 'jpeg', 'webp'] as const).map((format) => <button key={format} className={settings.format === format ? 'is-active' : ''} onClick={() => update('format', format)}><strong>{format === 'jpeg' ? 'JPEG' : format.toUpperCase()}</strong><small>{format === 'png' ? 'Lossless / Alpha' : format === 'jpeg' ? 'Lossy / Opaque' : 'Modern / Alpha'}</small></button>)}
            </div></div>
            <div className="modal-grid two-columns">
              <label className="field-label">出力範囲<select value={settings.bounds} onChange={(event) => update('bounds', event.target.value as CompositeBoundsMode)}><option value="selection">選択レイヤー範囲</option><option value="document">ドキュメント全体</option></select></label>
              <label className="field-label">解像度<select value={settings.scale} onChange={(event) => update('scale', Number(event.target.value))}><option value={0.5}>0.5×</option><option value={1}>1×（原寸）</option><option value={2}>2×</option></select></label>
            </div>
            <div className="modal-grid two-columns">
              <label className="field-label">背景<select value={settings.format === 'jpeg' && settings.background === 'transparent' ? 'white' : settings.background} disabled={settings.format === 'jpeg'} onChange={(event) => update('background', event.target.value as CompositeBackground)}><option value="transparent">透明</option><option value="white">白</option><option value="black">黒</option></select></label>
              {settings.format !== 'png' ? <label className="field-label">品質 <span>{Math.round(settings.quality * 100)}</span><input className="quality-range" type="range" min="0.4" max="1" step="0.01" value={settings.quality} onChange={(event) => update('quality', Number(event.target.value))} /></label> : <div className="field-note"><ShieldCheck size={14} />PNGは可逆圧縮です</div>}
            </div>
            <div className="render-options">
              <h3>レンダリング</h3>
              <label className="switch-row"><span><strong>レイヤーマスク</strong><small>ラスターマスクをアルファへ適用</small></span><input type="checkbox" checked={settings.applyMask} onChange={(event) => update('applyMask', event.target.checked)} /><i /></label>
              <label className="switch-row"><span><strong>不透明度</strong><small>PSDのレイヤー不透明度を適用</small></span><input type="checkbox" checked={settings.applyOpacity} onChange={(event) => update('applyOpacity', event.target.checked)} /><i /></label>
              <label className="switch-row"><span><strong>合成モード</strong><small>対応するPhotoshopブレンドを評価</small></span><input type="checkbox" checked={settings.applyBlendModes} onChange={(event) => update('applyBlendModes', event.target.checked)} /><i /></label>
              <label className="switch-row"><span><strong>ICCプロファイル</strong><small>{settings.format === 'png' && canEmbedProfile ? '元のRGBプロファイルを保持' : 'RGB PNGの原寸出力のみ利用可能'}</small></span><input type="checkbox" checked={settings.embedProfile} disabled={settings.format !== 'png' || !canEmbedProfile || settings.scale !== 1} onChange={(event) => update('embedProfile', event.target.checked)} /><i /></label>
            </div>
          </div>
          <aside className="modal-summary">
            <span className="summary-preview checkerboard"><ImageIcon size={28} /></span>
            <dl><div><dt>Layers</dt><dd>{selectedNodes.length}</dd></div><div><dt>Output</dt><dd>{outputWidth.toLocaleString()} × {outputHeight.toLocaleString()} px</dd></div><div><dt>Depth</dt><dd>{settings.format === 'png' && settings.scale === 1 ? `${document.psd.bitsPerChannel ?? 8} bit` : '8 bit'}</dd></div><div><dt>Alpha</dt><dd>{settings.format === 'jpeg' ? 'No' : settings.background === 'transparent' ? 'Yes' : 'Flattened'}</dd></div></dl>
            <p>上にあるレイヤーほど前面に合成されます。現在の並び順がそのまま出力へ反映されます。</p>
          </aside>
        </div>
        <footer className="modal-footer"><button onClick={onClose} disabled={exporting}>キャンセル</button><button className="primary-action" disabled={exporting || !settings.filename.trim()} onClick={() => void onExport(settings)}>{exporting ? <LoaderCircle className="spin" size={17} /> : <Download size={17} />}画像を書き出す</button></footer>
      </section>
    </div>
  )
}

interface WorkspaceProps {
  document: PsdDocument
  file: File
  onClose: () => void
}

function Workspace({ document, file, onClose }: WorkspaceProps) {
  const firstLayer = document.nodes.find((node) => !node.isGroup && node.layer.imageData)
  const [activeId, setActiveId] = useState(firstLayer?.id)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [orderedNodes, setOrderedNodes] = useState(document.nodes)
  const [draggedId, setDraggedId] = useState<string>()
  const [expanded, setExpanded] = useState<Set<string>>(new Set(document.nodes.filter((node) => node.isGroup).map((node) => node.id)))
  const [query, setQuery] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [notice, setNotice] = useState<string>()

  const activeNode = activeId ? document.nodeMap.get(activeId) : undefined
  const selectedLeafIds = useMemo(() => uniqueLeafIds(document, selected), [document, selected])
  const selectedIdSet = useMemo(() => new Set(selectedLeafIds), [selectedLeafIds])
  const selectedNodes = useMemo(() => orderedNodes.filter((node) => !node.isGroup && selectedIdSet.has(node.id)), [orderedNodes, selectedIdSet])
  const selectedWarnings = selectedNodes.filter((node) => node.compatibility === 'review').length

  const preview = useMemo(() => {
    if (selectedNodes.length) {
      try {
        return {
          image: composeLayers(selectedNodes, {
            bounds: 'selection',
            documentWidth: document.psd.width,
            documentHeight: document.psd.height,
            applyMask: true,
            applyOpacity: true,
            applyBlendModes: true,
            background: 'transparent',
          }).image,
          label: `Composite preview · ${selectedNodes.length} layer${selectedNodes.length > 1 ? 's' : ''}`,
        }
      } catch {
        return { image: undefined, label: 'Composite preview unavailable' }
      }
    }
    return {
      image: activeNode && !activeNode.isGroup ? renderLayerPixels(activeNode.layer, true, true) : undefined,
      label: activeNode ? `Isolated preview · ${activeNode.name}` : 'レイヤーを選択してください',
    }
  }, [activeNode, document.psd.height, document.psd.width, selectedNodes])

  const visibleNodes = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('ja')
    if (normalized) return orderedNodes.filter((node) => node.path.join('/').toLocaleLowerCase('ja').includes(normalized))
    const result: LayerNode[] = []
    const hiddenDepths = new Set<number>()
    orderedNodes.forEach((node) => {
      for (const depth of [...hiddenDepths]) if (depth >= node.depth) hiddenDepths.delete(depth)
      if (hiddenDepths.size === 0) result.push(node)
      if (node.isGroup && !expanded.has(node.id) && hiddenDepths.size === 0) hiddenDepths.add(node.depth)
    })
    return result
  }, [expanded, orderedNodes, query])

  const toggleSelection = (node: LayerNode) => {
    setSelected((current) => {
      const next = new Set(current)
      const ids = node.isGroup ? node.descendants : [node.id]
      const allSelected = ids.every((id) => next.has(id))
      ids.forEach((id) => { if (allSelected) next.delete(id); else next.add(id) })
      return next
    })
  }

  const reorderNodes = (sourceId: string, targetId: string, after: boolean) => {
    if (sourceId === targetId) return
    setOrderedNodes((current) => {
      const sourceIndex = current.findIndex((node) => node.id === sourceId)
      const targetIndex = current.findIndex((node) => node.id === targetId)
      if (sourceIndex < 0 || targetIndex < 0) return current
      const source = current[sourceIndex]
      const target = current[targetIndex]
      const sourceParent = source.path.slice(0, -1).join('\u0000')
      const targetParent = target.path.slice(0, -1).join('\u0000')
      if (source.depth !== target.depth || sourceParent !== targetParent) {
        setNotice('レイヤーは同じグループ階層内で並び替えてください')
        return current
      }
      let sourceEnd = sourceIndex + 1
      while (sourceEnd < current.length && current[sourceEnd].depth > source.depth) sourceEnd += 1
      if (targetIndex >= sourceIndex && targetIndex < sourceEnd) return current
      const block = current.slice(sourceIndex, sourceEnd)
      const rest = [...current.slice(0, sourceIndex), ...current.slice(sourceEnd)]
      const nextTargetIndex = rest.findIndex((node) => node.id === targetId)
      if (nextTargetIndex < 0) return current
      let insertion = nextTargetIndex
      if (after) {
        insertion += 1
        while (insertion < rest.length && rest[insertion].depth > target.depth) insertion += 1
      }
      setNotice('レイヤー順を変更しました')
      return [...rest.slice(0, insertion), ...block, ...rest.slice(insertion)]
    })
  }

  const exportComposite = async (settings: ExportSettings) => {
    setExporting(true)
    setNotice(undefined)
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 20))
      const background = settings.format === 'jpeg' && settings.background === 'transparent' ? 'white' : settings.background
      const result = composeLayers(selectedNodes, {
        bounds: settings.bounds,
        documentWidth: document.psd.width,
        documentHeight: document.psd.height,
        applyMask: settings.applyMask,
        applyOpacity: settings.applyOpacity,
        applyBlendModes: settings.applyBlendModes,
        background,
      })
      const baseName = safeFilename(settings.filename, 'composite')
      if (settings.format === 'png') {
        const image = settings.scale === 1
          ? result.image
          : (() => {
              const canvas = canvasFromPixels(result.image, settings.scale)
              const context = canvas.getContext('2d')
              if (!context) throw new Error('画像をリサイズできませんでした。')
              return { width: canvas.width, height: canvas.height, data: context.getImageData(0, 0, canvas.width, canvas.height).data }
            })()
        downloadBytes(encodePng(image, {
          iccProfile: settings.embedProfile && settings.scale === 1 && document.psd.colorMode === 3 ? document.iccProfile : undefined,
          profileName: document.iccProfileName,
          resolutionPpi: document.psd.imageResources?.resolutionInfo?.horizontalResolution,
        }), `${baseName}.png`, 'image/png')
      } else {
        const canvas = canvasFromPixels(result.image, settings.scale)
        const mime = settings.format === 'jpeg' ? 'image/jpeg' : 'image/webp'
        downloadBlob(await canvasToBlob(canvas, mime, settings.quality), `${baseName}.${settings.format === 'jpeg' ? 'jpg' : 'webp'}`)
      }
      const blendWarning = result.approximatedBlendModes.length ? `（${result.approximatedBlendModes.join(', ')}は通常合成で近似）` : ''
      setNotice(`合成画像を書き出しました${blendWarning}`)
      setModalOpen(false)
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : '書き出しに失敗しました')
    } finally {
      setExporting(false)
    }
  }

  const compatibility = documentCompatibility(document)
  const bitDepth = document.psd.bitsPerChannel ?? 8
  const selectionBounds = selectedNodes.length ? getCompositeBounds(selectedNodes, 'selection', document.psd.width, document.psd.height) : undefined

  return (
    <main className="workspace">
      <header className="app-header">
        <div className="brand compact"><span className="brand-mark"><Layers3 size={17} /></span>Unlayer</div>
        <span className="header-divider" />
        <div className="file-title"><strong>{file.name}</strong><span>{formatBytes(file.size)} · {document.psd.width.toLocaleString()} × {document.psd.height.toLocaleString()} px</span></div>
        <div className="header-actions"><span className="local-badge"><LockKeyhole size={12} /> Local only</span><button className="close-file" onClick={onClose}><RotateCcw size={15} /> 別のPSDを開く</button></div>
      </header>
      <div className="workbench">
        <aside className="layers-panel panel">
          <div className="panel-heading"><div><span className="panel-kicker">DOCUMENT</span><h2>Layers <em>{document.nodes.filter((node) => !node.isGroup).length}</em></h2></div><button className="select-all" onClick={() => { const all = document.nodes.filter((node) => !node.isGroup && node.compatibility !== 'unsupported').map((node) => node.id); setSelected(selectedLeafIds.length === all.length ? new Set() : new Set(all)) }}>{selectedLeafIds.length ? '選択解除' : 'すべて選択'}</button></div>
          <label className="search-box"><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="レイヤーを検索" />{query && <button onClick={() => setQuery('')}><X size={13} /></button>}</label>
          <div className="stack-hint"><GripVertical size={11} /> 同じ階層内でドラッグして順序変更</div>
          <div className="layer-list">
            {visibleNodes.map((node) => <LayerRow key={node.id} node={node} active={node.id === activeId} selected={node.isGroup ? node.descendants.length > 0 && node.descendants.every((id) => selected.has(id)) : selected.has(node.id)} expanded={expanded.has(node.id)} reorderEnabled={!query} dragging={draggedId === node.id} onActivate={() => { if (!node.isGroup && node.layer.imageData) setActiveId(node.id); if (node.isGroup) setExpanded((current) => { const next = new Set(current); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next }) }} onToggle={() => toggleSelection(node)} onExpand={() => setExpanded((current) => { const next = new Set(current); if (next.has(node.id)) next.delete(node.id); else next.add(node.id); return next })} onDragStart={() => setDraggedId(node.id)} onDragEnd={() => setDraggedId(undefined)} onDrop={(after) => { if (draggedId) reorderNodes(draggedId, node.id, after); setDraggedId(undefined) }} />)}
          </div>
          <div className="selection-footer"><span>{selectedLeafIds.length}</span> selected for composite</div>
        </aside>

        <CanvasPreview rendered={preview.image} label={preview.label} />

        <aside className="export-panel panel">
          <div className="panel-heading inspector-heading"><div><span className="panel-kicker">INSPECTOR</span><h2>Composite export</h2></div><Settings2 size={16} /></div>
          <section className="inspector-section document-summary"><div className="summary-icon"><FileImage size={18} /></div><div><strong>{colorModeName(document.psd.colorMode)} · {bitDepth} bit</strong><span>{document.iccProfileName || (document.iccProfile ? 'Embedded ICC profile' : 'sRGB assumed')}</span></div>{compatibility !== 'exact' && <span className="compatibility-pill is-review">Review</span>}</section>
          <section className="inspector-section selection-summary"><h3>Selection</h3><dl><div><dt>Layers</dt><dd>{selectedNodes.length}</dd></div><div><dt>Bounds</dt><dd>{selectionBounds ? `${selectionBounds.width.toLocaleString()} × ${selectionBounds.height.toLocaleString()} px` : '—'}</dd></div><div><dt>Order</dt><dd>Top → Bottom</dd></div></dl><p>チェックしたレイヤーを現在のスタック順で1枚の画像へ合成します。</p></section>
          <section className="inspector-section active-details"><h3>Active layer</h3>{activeNode ? <><div className="detail-name"><LayerThumbnail layer={activeNode.layer} /><span><strong>{activeNode.name}</strong><small>{activeNode.path.join(' / ')}</small></span></div><dl><div><dt>Bounds</dt><dd>{activeNode.layer.imageData ? `${activeNode.layer.imageData.width} × ${activeNode.layer.imageData.height} px` : '—'}</dd></div><div><dt>Blend</dt><dd>{activeNode.layer.blendMode ?? 'normal'}</dd></div><div><dt>Opacity</dt><dd>{Math.round((activeNode.layer.opacity ?? 1) * 100)}%</dd></div></dl>{activeNode.compatibility !== 'exact' && <div className={`layer-compatibility ${activeNode.compatibility}`}><AlertTriangle size={15} /><span>{activeNode.compatibilityNote}</span></div>}</> : <p>レイヤーを選ぶと詳細を表示します。</p>}</section>
          <div className="export-footer">{selectedWarnings > 0 && <div className="export-warning"><AlertTriangle size={14} /> {selectedWarnings}件は目視確認を推奨</div>}{notice && <div className="export-notice">{notice}</div>}<button className="export-button" disabled={!selectedNodes.length} onClick={() => setModalOpen(true)}><Settings2 size={18} />{selectedNodes.length ? `${selectedNodes.length}レイヤーを合成…` : 'レイヤーを選択'}</button><span>PNG · JPEG · WebP</span></div>
        </aside>
      </div>
      {modalOpen && <ExportModal document={document} file={file} selectedNodes={selectedNodes} exporting={exporting} onClose={() => setModalOpen(false)} onExport={exportComposite} />}
    </main>
  )
}

function App() {
  const [document, setDocument] = useState<PsdDocument>()
  const [file, setFile] = useState<File>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const openFile = async (nextFile: File) => {
    if (!/\.(psd|psb)$/i.test(nextFile.name)) { setError('PSDまたはPSBファイルを選択してください。'); return }
    setLoading(true)
    setError(undefined)
    try {
      const source = await nextFile.arrayBuffer()
      const { readPsd } = await import('ag-psd')
      const psd = readPsd(source.slice(0), { useImageData: true, skipThumbnail: true, skipLinkedFilesData: true, logMissingFeatures: false, throwForMissingFeatures: false, totalMemoryLimit: Math.min(2_000_000_000, Math.max(nextFile.size * 20, 256_000_000)) })
      const parsed = buildDocument(psd, source)
      if (!parsed.nodes.length) throw new Error('レイヤーが見つかりませんでした。統合画像のみのPSDかもしれません。')
      setFile(nextFile)
      setDocument(parsed)
    } catch (reason) {
      setError(reason instanceof Error ? `ファイルを開けませんでした: ${reason.message}` : 'ファイルを開けませんでした。')
    } finally {
      setLoading(false)
    }
  }

  if (document && file) return <Workspace document={document} file={file} onClose={() => { setDocument(undefined); setFile(undefined); setError(undefined) }} />
  return <EmptyWorkspace onFile={openFile} onSample={() => { void createSampleFile().then(openFile) }} error={error} loading={loading} />
}

export default App
